<script setup>
import { computed, defineModel, h, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterSelect from './inputs/FilterSelect.vue';
import MultiSelect from './inputs/MultiSelect.vue';
import SingleSelect from './inputs/SingleSelect.vue';

import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { validateSingleFilter } from 'dashboard/helper/validations.js';

// filterTypes: import('vue').ComputedRef<FilterType[]>
const props = defineProps({
  showQueryOperator: { type: Boolean, default: false },
  // When set (e.g. 'and'), only that operator is shown — no AND/OR chooser.
  fixedQueryOperator: {
    type: String,
    default: null,
    validator: value => value == null || ['and', 'or'].includes(value),
  },
  filterTypes: { type: Array, required: true },
  searchableAttributes: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['remove']);
const { t } = useI18n();
const showErrors = ref(false);

const attributeKey = defineModel('attributeKey', {
  type: String,
  required: true,
});

const values = defineModel('values', {
  type: [String, Number, Array, Object],
  required: true,
});

const filterOperator = defineModel('filterOperator', {
  type: String,
  required: true,
});

const queryOperator = defineModel('queryOperator', {
  type: String,
  required: false,
  default: undefined,
  validator: value => ['and', 'or'].includes(value),
});

const getFilterFromFilterTypes = key =>
  props.filterTypes.find(filterObj => filterObj.attributeKey === key);

const currentFilter = computed(() =>
  getFilterFromFilterTypes(attributeKey.value)
);

const getOperator = (filter, selectedOperator) => {
  const operatorFromOptions = filter?.filterOperators?.find(
    operator => operator.value === selectedOperator
  );

  if (!operatorFromOptions) {
    return filter?.filterOperators?.[0];
  }

  return operatorFromOptions;
};

const currentOperator = computed(() =>
  getOperator(currentFilter.value, filterOperator.value)
);

const hasSingleFilterOperator = computed(
  () => (currentFilter.value?.filterOperators || []).length === 1
);

const hasSingleAttribute = computed(() => props.filterTypes.length === 1);

const getInputType = (operator, filter) =>
  operator.inputOverride ?? filter.inputType;

const inputType = computed(() =>
  getInputType(currentOperator.value, currentFilter.value)
);

const queryOperatorOptions = computed(() => [
  {
    label: t(`FILTER.QUERY_DROPDOWN_LABELS.AND`),
    value: 'and',
    icon: h('span', { class: 'i-lucide-ampersands !text-n-woot-text' }),
  },
  {
    label: t(`FILTER.QUERY_DROPDOWN_LABELS.OR`),
    value: 'or',
    icon: h('span', { class: 'i-woot-logic-or !text-n-woot-text' }),
  },
]);

const fixedQueryOperatorLabel = computed(() => {
  if (!props.fixedQueryOperator) return '';
  const label =
    queryOperatorOptions.value.find(
      option => option.value === props.fixedQueryOperator
    )?.label || '';
  // Dropdown uses all-caps ("AND"); fixed display uses title case ("And").
  return label ? label.charAt(0) + label.slice(1).toLowerCase() : '';
});

watch(
  () => props.fixedQueryOperator,
  fixed => {
    if (fixed) queryOperator.value = fixed;
  },
  { immediate: true }
);

const booleanOptions = computed(() => [
  { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
  { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
]);

const validationError = computed(() => {
  // TOOD: Migrate validateSingleFilter to use camelcase and then remove useSnakeCase here too
  return validateSingleFilter(
    useSnakeCase({
      attributeKey: attributeKey.value,
      filterOperator: filterOperator.value,
      values: values.value,
    })
  );
});

const inputFieldType = computed(() => {
  if (inputType.value === 'date') return 'date';
  if (inputType.value === 'time') return 'time';
  if (inputType.value === 'number') return 'number';
  return 'text';
});

const inputPlaceholder = computed(() => {
  if (
    ['days_before', 'within_next', 'within_last'].includes(filterOperator.value)
  ) {
    return t('FILTER.DAYS_PLACEHOLDER');
  }
  return t('FILTER.INPUT_PLACEHOLDER');
});

const resetModelOnAttributeKeyChange = newAttributeKey => {
  /**
   * Resets the filter values and operator when the attribute key changes. This ensures that
   * the values and operator remain compatible with the new attribute type. For example,
   * switching from a text field to a multi-select should reset the value from '' (empty string)
   * to an empty array.
   */
  const filter = getFilterFromFilterTypes(newAttributeKey);
  const newOperator = getOperator(filter, filterOperator.value);
  const newInputType = getInputType(newOperator, filter);
  if (newInputType === 'multiSelect') {
    values.value = [];
  } else if (['searchSelect', 'booleanSelect'].includes(newInputType)) {
    values.value = {};
  } else {
    values.value = '';
  }
  filterOperator.value = newOperator.value;
};

watch([attributeKey, values, filterOperator], () => {
  showErrors.value = false;
});

const validate = () => {
  showErrors.value = true;
  return !validationError.value;
};

const resetValidation = () => {
  showErrors.value = false;
};

const enableAttributeSearch = computed(
  () => props.searchableAttributes || props.filterTypes.length > 8
);

const attributeSelectOptions = computed(() =>
  props.filterTypes.map(filterType => ({
    value: filterType.value ?? filterType.attributeKey,
    label: filterType.label ?? filterType.attributeName,
    icon: filterType.icon,
    disabled: filterType.disabled,
  }))
);

defineExpose({ validate, resetValidation });
</script>

<template>
  <li class="list-none">
    <div
      class="flex items-center gap-2 rounded-md"
      :class="{
        'animate-wiggle': showErrors && validationError,
        'pointer-events-none opacity-80': readOnly,
      }"
    >
      <FilterSelect
        v-if="showQueryOperator && !fixedQueryOperator"
        v-model="queryOperator"
        variant="faded"
        hide-icon
        class="text-sm"
        :options="queryOperatorOptions"
      />
      <span
        v-else-if="showQueryOperator"
        class="shrink-0 px-1 text-sm text-n-slate-11"
      >
        {{ fixedQueryOperatorLabel }}
      </span>
      <FilterSelect
        v-if="!hasSingleAttribute"
        v-model="attributeKey"
        variant="faded"
        :searchable="enableAttributeSearch"
        :options="attributeSelectOptions"
        @update:model-value="resetModelOnAttributeKeyChange"
      />
      <span
        v-else
        class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-n-alpha-2 px-2.5 py-1.5 text-sm text-n-slate-12"
      >
        <component
          :is="currentFilter?.icon"
          v-if="currentFilter?.icon"
          class="size-3.5 shrink-0"
        />
        {{ currentFilter?.label || currentFilter?.attributeName }}
      </span>
      <FilterSelect
        v-if="!hasSingleFilterOperator"
        v-model="filterOperator"
        variant="ghost"
        :options="currentFilter?.filterOperators"
      />
      <span
        v-else
        class="inline-flex shrink-0 items-center gap-1.5 px-1 text-sm text-n-slate-11"
      >
        <component
          :is="currentOperator?.icon"
          v-if="currentOperator?.icon"
          class="size-3.5 shrink-0"
        />
        {{ currentOperator?.label }}
      </span>
      <template v-if="currentOperator?.hasInput">
        <MultiSelect
          v-if="inputType === 'multiSelect'"
          v-model="values"
          :options="currentFilter.options"
          dropdown-max-height="max-h-72"
        />
        <SingleSelect
          v-else-if="inputType === 'searchSelect'"
          v-model="values"
          :options="currentFilter.options"
          dropdown-max-height="max-h-64"
        />
        <SingleSelect
          v-else-if="inputType === 'booleanSelect'"
          v-model="values"
          disable-search
          :options="booleanOptions"
        />
        <Input
          v-else
          v-model="values"
          :type="inputFieldType"
          class="[&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
          :placeholder="inputPlaceholder"
        />
      </template>
      <Button
        v-if="!readOnly"
        type="button"
        sm
        solid
        slate
        icon="i-lucide-trash-2"
        @click.stop="emit('remove')"
      />
    </div>
    <span v-if="showErrors && validationError" class="text-sm text-n-ruby-11">
      {{ t(`FILTER.ERRORS.${validationError}`) }}
    </span>
  </li>
</template>
